import Experience from "@folio-2026/experience"
import { EVENTS } from "@folio-2026/shared"
import gsap from "gsap"
import {
  cameraPosition,
  float,
  Fn,
  instancedBufferAttribute,
  length,
  mix,
  positionWorld,
  smoothstep,
  step,
  uv,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import Grid from "./Grid"

export default class GridLines {
  scene: THREE.Scene
  debugFolder?: FolderApi
  experience: Experience
  grid: Grid
  instancedMesh?: THREE.InstancedMesh
  paths: THREE.Vector3[][] = []
  pathStartIndices: number[] = [] // Starting instance index for each path
  segmentIndexAttribute?: THREE.InstancedBufferAttribute // Order of segment in path (0, 1, 2, ...)
  pathIndexAttribute?: THREE.InstancedBufferAttribute // Which path this segment belongs to (0-9)
  pathProgressAttribute?: THREE.InstancedBufferAttribute // Progress value for each path (0-1)
  totalSegmentsPerPath: number[] = [] // Total segments count for each path
  pathProgressValues: number[] = [] // Current progress value for each path (0-1)
  pathAnimations: gsap.core.Tween[] = [] // GSAP animations for each path
  maxSegments: number = 1 // Cached maximum segments count across all paths
  private rafId: number | null = null // RequestAnimationFrame ID for batched updates

  // Reusable objects to avoid allocations in hot paths
  private tempVector = new THREE.Vector3()
  private tempDirection = new THREE.Vector3()
  private tempBaseDirection = new THREE.Vector3(1, 0, 0)
  private tempQuaternion = new THREE.Quaternion()
  private tempScale = new THREE.Vector3()
  private tempMatrix = new THREE.Matrix4()
  private tempWorldPos = new THREE.Vector3()

  settings = {
    delayMin: 0, // Minimum delay in seconds
    delayMax: 2, // Maximum delay in seconds
    durationMin: 15, // Minimum duration in seconds
    durationMax: 60, // Maximum duration in seconds
  }

  constructor({
    scene,
    debugFolder,
    grid,
  }: {
    scene: THREE.Scene
    debugFolder?: FolderApi
    grid: Grid
  }) {
    this.experience = Experience.getInstance()
    this.scene = scene
    this.debugFolder = debugFolder
    this.grid = grid

    this.settings = {
      ...this.settings,
    }

    this.setMesh()
    this.setDebug()
    this.listenEvents()
  }

  listenEvents() {
    this.experience.on(EVENTS.Guestbook.BeforeEnter, () => {
      // Kill all existing animations (pause them)
      this.pathAnimations.forEach((anim) => anim.kill())
      this.pathAnimations = []

      // Reset all progress values to 0
      this.pathProgressValues.fill(0)
      this.updatePathProgressAttribute()
    })

    this.experience.on(
      EVENTS.Guestbook.Entering,
      this.onEnterAnimation.bind(this),
    )
  }

  onEnterAnimation(event: unknown) {
    const { progress } = event as { progress: number }
    if (progress === 1) {
      this.animatePaths()
    }
  }

  /**
   * Updates the path progress attribute with current progress values
   * Optimized to directly update the attribute array without intermediate arrays
   */
  private updatePathProgressAttribute() {
    if (!this.instancedMesh || !this.pathProgressAttribute) {
      return
    }

    const array = this.pathProgressAttribute.array as Float32Array
    let arrayIndex = 0

    // Directly update the attribute array for each path's segments
    for (let pathIndex = 0; pathIndex < this.paths.length; pathIndex++) {
      const path = this.paths[pathIndex]
      const segmentCount = path.length - 1
      const progressValue = this.pathProgressValues[pathIndex]

      // Assign the same progress value to all segments of this path
      for (let i = 0; i < segmentCount; i++) {
        array[arrayIndex++] = progressValue
      }
    }

    this.pathProgressAttribute.needsUpdate = true
  }

  /**
   * Animates all paths with GSAP, each with random delay and duration
   * Optimized to batch attribute updates using requestAnimationFrame
   */
  private animatePaths() {
    // Kill any existing animations
    this.pathAnimations.forEach((anim) => anim.kill())
    this.pathAnimations = []

    // Reset all progress values to 0
    this.pathProgressValues.fill(0)
    this.updatePathProgressAttribute()

    // Track if we need to update the attribute (batched updates)
    let needsUpdate = false

    const scheduleUpdate = () => {
      if (!needsUpdate && this.rafId === null) {
        this.rafId = requestAnimationFrame(() => {
          if (needsUpdate) {
            this.updatePathProgressAttribute()
            needsUpdate = false
          }
          this.rafId = null
        })
      }
      needsUpdate = true
    }

    // Create animation for each path
    for (let pathIndex = 0; pathIndex < this.paths.length; pathIndex++) {
      // Generate random delay and duration within specified ranges
      const delay =
        this.settings.delayMin +
        Math.random() * (this.settings.delayMax - this.settings.delayMin)
      const duration =
        this.settings.durationMin +
        Math.random() * (this.settings.durationMax - this.settings.durationMin)

      // Create a proxy object for this path's progress
      const progressProxy = { value: 0 }

      // Create GSAP animation
      const animation = gsap.to(progressProxy, {
        value: 1,
        duration,
        delay,
        ease: "power2.out",
        repeat: -1,
        onUpdate: () => {
          // Update the progress value for this path
          this.pathProgressValues[pathIndex] = progressProxy.value
          // Schedule batched attribute update
          scheduleUpdate()
        },
      })

      this.pathAnimations.push(animation)
    }
  }

  /**
   * Converts grid coordinates to world position
   * Reuses tempWorldPos to avoid allocations
   */
  private gridToWorld(x: number, y: number, z: number): THREE.Vector3 {
    const { xSize, spacing, zStart } = this.grid.settings
    const xOffset = (xSize - 1) / 2
    return this.tempWorldPos
      .set((x - xOffset) * spacing, y * spacing, (zStart - z) * spacing)
      .clone() // Clone since we reuse the temp object
  }

  /**
   * Clamps a coordinate to grid bounds
   */
  private clampToGrid(value: number, direction: "x" | "y" | "z"): number {
    const { xSize, ySize, zSize } = this.grid.settings
    const max =
      direction === "x" ? xSize - 1 : direction === "y" ? ySize - 1 : zSize - 1
    return Math.max(0, Math.min(max, value))
  }

  /**
   * Generates a random path through the grid with direction changes
   * Paths only extend in negative z direction (world space) by only allowing +z movement in grid coordinates
   * Paths start at grid z > zStart to ensure negative world z (below z: 0)
   * @returns Array of 3D world positions representing the path
   */
  generateRandomPath() {
    const { xSize, ySize, zSize, zStart } = this.grid.settings

    // Start at a random grid point
    // For world z to be negative: (zStart - z) * spacing < 0 => z > zStart
    // Start paths at grid z values greater than zStart to ensure negative world z
    const minZ = zStart + 1 // Start after zStart to ensure negative world z
    const maxZ = zSize - 1 // Maximum grid z value
    let currentX = Math.floor(Math.random() * xSize)
    let currentY = Math.floor(Math.random() * ySize)
    let currentZ =
      minZ + Math.floor(Math.random() * Math.max(1, maxZ - minZ + 1))

    const path = [this.gridToWorld(currentX, currentY, currentZ)]

    const numSegments = 3 + Math.floor(Math.random() * 12) // 3 to 15 segments
    let lastDirection: "x" | "y" | "z" | null = null

    for (let i = 0; i < numSegments; i++) {
      const directions: Array<"x" | "y" | "z"> = ["x", "y", "z"]
      const availableDirections = directions.filter((d) => d !== lastDirection)
      const direction =
        availableDirections[
          Math.floor(Math.random() * availableDirections.length)
        ]

      const stepSize = 1 + Math.floor(Math.random() * 3)
      // For z direction, only allow positive steps (negative z in world space)
      // For x and y, allow both directions
      const stepSign = direction === "z" ? 1 : Math.random() > 0.5 ? 1 : -1

      const newX =
        direction === "x"
          ? this.clampToGrid(currentX + stepSize * stepSign, "x")
          : currentX
      const newY =
        direction === "y"
          ? this.clampToGrid(currentY + stepSize * stepSign, "y")
          : currentY
      const newZ =
        direction === "z"
          ? this.clampToGrid(currentZ + stepSize * stepSign, "z")
          : currentZ

      // Ensure z only increases (moves in negative z in world space)
      if (direction === "z" && newZ <= currentZ) {
        continue // Skip this iteration if z didn't increase
      }

      if (newX !== currentX || newY !== currentY || newZ !== currentZ) {
        currentX = newX
        currentY = newY
        currentZ = newZ
        path.push(this.gridToWorld(currentX, currentY, currentZ))
        lastDirection = direction
      }
    }

    return path
  }

  /**
   * Creates a transformation matrix for a line segment
   * Positions segment at start point so it extends to end point, ensuring connectivity
   * Reuses temporary objects to avoid allocations
   */
  private createSegmentMatrix(
    start: THREE.Vector3,
    end: THREE.Vector3,
    spacing: number,
  ): THREE.Matrix4 {
    // Reuse temp objects
    this.tempVector.subVectors(end, start)
    const segmentLength = this.tempVector.length()
    this.tempDirection.copy(this.tempVector).normalize()

    this.tempQuaternion.setFromUnitVectors(
      this.tempBaseDirection,
      this.tempDirection,
    )
    this.tempScale.set(segmentLength / spacing, 1, 1)

    // Position at start point (not midpoint) so segment extends from start to end
    // This ensures segments connect at their endpoints when direction changes
    return this.tempMatrix
      .compose(start, this.tempQuaternion, this.tempScale)
      .clone() // Clone since we reuse the temp matrix
  }

  /**
   * Creates and updates instanced buffer attributes for segment indices, path indices, and path progress
   */
  private updateSegmentIndices() {
    if (!this.instancedMesh) {
      return
    }

    const segmentIndices: number[] = []
    const pathIndices: number[] = []
    const pathProgresses: number[] = []
    this.totalSegmentsPerPath = []

    // Initialize progress values for each path
    if (this.pathProgressValues.length !== this.paths.length) {
      this.pathProgressValues = new Array(this.paths.length).fill(0)
    }

    // Clear arrays to reuse them
    segmentIndices.length = 0
    pathIndices.length = 0
    pathProgresses.length = 0
    this.totalSegmentsPerPath.length = 0

    for (let pathIndex = 0; pathIndex < this.paths.length; pathIndex++) {
      const path = this.paths[pathIndex]
      const segmentCount = path.length - 1
      this.totalSegmentsPerPath.push(segmentCount)

      // Assign index to each segment (0, 1, 2, ...), path index, and path progress
      for (let i = 0; i < segmentCount; i++) {
        segmentIndices.push(i)
        pathIndices.push(pathIndex)
        pathProgresses.push(this.pathProgressValues[pathIndex])
      }
    }

    // Update cached maxSegments
    this.maxSegments = Math.max(...this.totalSegmentsPerPath, 1)

    this.segmentIndexAttribute = new THREE.InstancedBufferAttribute(
      new Float32Array(segmentIndices),
      1,
    )
    this.pathIndexAttribute = new THREE.InstancedBufferAttribute(
      new Float32Array(pathIndices),
      1,
    )
    this.pathProgressAttribute = new THREE.InstancedBufferAttribute(
      new Float32Array(pathProgresses),
      1,
    )

    this.instancedMesh.geometry.setAttribute(
      "segmentIndex",
      this.segmentIndexAttribute,
    )
    this.instancedMesh.geometry.setAttribute(
      "pathIndex",
      this.pathIndexAttribute,
    )
    this.instancedMesh.geometry.setAttribute(
      "pathProgress",
      this.pathProgressAttribute,
    )
  }

  /**
   * Updates transformation matrices for all paths in the InstancedMesh
   */
  private updatePathMatrices() {
    if (!this.instancedMesh) {
      return
    }

    const { spacing } = this.grid.settings
    let instanceIndex = 0

    for (const path of this.paths) {
      for (let i = 0; i < path.length - 1; i++) {
        const matrix = this.createSegmentMatrix(path[i], path[i + 1], spacing)
        this.instancedMesh.setMatrixAt(instanceIndex++, matrix)
      }
    }

    this.instancedMesh.instanceMatrix.needsUpdate = true
  }

  /**
   * Disposes and removes the existing mesh
   */
  private disposeMesh() {
    // Cancel any pending requestAnimationFrame
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }

    // Kill all GSAP animations
    this.pathAnimations.forEach((anim) => anim.kill())
    this.pathAnimations = []

    if (!this.instancedMesh) {
      return
    }

    this.scene.remove(this.instancedMesh)
    this.instancedMesh.geometry.dispose()
    const material = this.instancedMesh.material
    if (Array.isArray(material)) {
      material.forEach((m) => m.dispose())
    } else {
      material.dispose()
    }
  }

  /**
   * Counts total segments and calculates path start indices
   */
  private calculatePathIndices(): number {
    let totalSegments = 0
    this.pathStartIndices = []
    for (const path of this.paths) {
      this.pathStartIndices.push(totalSegments)
      totalSegments += path.length - 1
    }
    return totalSegments
  }

  /**
   * Creates the base geometry for a line segment
   */
  private createBaseGeometry(spacing: number, lineThickness: number) {
    const geometry = new THREE.BufferGeometry()
    const positions = new Float32Array([
      0,
      -lineThickness / 2,
      0,
      spacing,
      -lineThickness / 2,
      0,
      spacing,
      lineThickness / 2,
      0,
      0,
      -lineThickness / 2,
      0,
      spacing,
      lineThickness / 2,
      0,
      0,
      lineThickness / 2,
      0,
    ])

    // Add UV coordinates: u goes from 0 to 1 along the segment length (X axis)
    const uvs = new Float32Array([
      0,
      0, // bottom-left
      1,
      0, // bottom-right
      1,
      1, // top-right
      0,
      0, // bottom-left
      1,
      1, // top-right
      0,
      1, // top-left
    ])

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2))
    return geometry
  }

  /**
   * Updates the material's opacity shader based on progress, segment order, and position along segment
   */
  private updateOpacityShader(material: THREE.NodeMaterial) {
    if (
      !this.segmentIndexAttribute ||
      !this.pathIndexAttribute ||
      !this.pathProgressAttribute ||
      this.totalSegmentsPerPath.length === 0
    ) {
      return
    }

    const segmentIndexAttr = instancedBufferAttribute(
      this.segmentIndexAttribute,
    )
    const pathProgressAttr = instancedBufferAttribute(
      this.pathProgressAttribute,
    )

    // Get normalized position along the segment (0 to 1) using UV coordinates
    // UV u coordinate goes from 0 to 1 along the segment length
    const segmentLocalPos = uv().x

    // Calculate the "reveal position" for this segment
    // segmentIndex + segmentLocalPos gives us the exact reveal position within the path
    // segmentIndex is the order (0, 1, 2, ...), segmentLocalPos is position along that segment (0-1)
    const segmentRevealPos = segmentIndexAttr.add(segmentLocalPos)

    // Use cached maximum segments count across all paths
    // This normalizes progress so all paths reveal at the same rate
    const totalSegmentsFloat = float(this.maxSegments)

    // Use per-path progress from instanced attribute
    const progress = pathProgressAttr

    // Determine which phase we're in: reveal (0-0.5) or hide (0.5-1)
    const isHidePhase = step(float(0.5), progress) // 0 if progress < 0.5, 1 if progress >= 0.5

    // Calculate effective progress for each phase:
    // - Reveal phase (0-0.5): map progress 0-0.5 to 0-1 (multiply by 2)
    // - Hide phase (0.5-1): map progress 0.5-1 to 0-1 ((progress - 0.5) * 2)
    const revealProgress = progress.mul(float(2)) // 0-0.5 → 0-1
    const hideProgress = progress.sub(float(0.5)).mul(float(2)) // 0.5-1 → 0-1
    const effectiveProgress = mix(revealProgress, hideProgress, isHidePhase)

    // Calculate progress in terms of segments
    const currentSegmentProgress = effectiveProgress.mul(totalSegmentsFloat)

    // For reveal: segment visible if segmentRevealPos < currentSegmentProgress
    // For hide: segment invisible if segmentRevealPos < currentSegmentProgress
    const fadeWidth = float(0.15) // Width of fade transition
    const fadeStart = currentSegmentProgress.sub(fadeWidth)
    const fadeEnd = currentSegmentProgress

    // Calculate opacity based on phase
    // Reveal: opacity = 1 when segmentRevealPos < currentSegmentProgress
    // Hide: opacity = 0 when segmentRevealPos < currentSegmentProgress
    const revealOpacity = smoothstep(
      fadeStart,
      fadeEnd,
      segmentRevealPos,
    ).oneMinus()
    const hideOpacity = smoothstep(fadeStart, fadeEnd, segmentRevealPos) // Inverted for hide

    // Blend between reveal and hide opacity based on phase
    const pathOpacity = mix(revealOpacity, hideOpacity, isHidePhase)

    // Apply distance-based opacity (same logic as Grid component)
    // This hides the grid lines when too close or too far from camera
    const distanceOpacity = Fn(() => {
      const distanceFromCamera = length(positionWorld.sub(cameraPosition))

      // farOpacity: fades out as distance increases
      // When distance <= minDistance: fully visible (opacity = 1)
      // When distance >= maxDistance: invisible (opacity = 0)
      const farOpacity = smoothstep(
        this.grid.settings.minDistance,
        this.grid.settings.maxDistance,
        distanceFromCamera,
      ).oneMinus()
      const closeOpacity = smoothstep(
        this.grid.settings.hideUntilDistance,
        this.grid.settings.minDistance,
        distanceFromCamera,
      )

      return farOpacity.mul(closeOpacity)
    })()

    // Combine path opacity with distance-based opacity
    const opacity = pathOpacity.mul(distanceOpacity)

    material.opacityNode = opacity

    // Apply the same opacity to emissive so it follows the path animation
    const outputColor = this.grid.settings.color
    ;(material as unknown as THREE.MeshPhysicalNodeMaterial).emissiveNode =
      outputColor.mul(0.4).mul(opacity)
  }

  /**
   * Creates the material for the grid lines
   */
  private createMaterial() {
    const outputColor = this.grid.settings.color

    const material = new THREE.NodeMaterial()
    material.transparent = true
    material.colorNode = outputColor
    ;(material as unknown as THREE.MeshPhysicalNodeMaterial).emissiveNode =
      outputColor.mul(0.4)

    // Opacity will be set by updateOpacityShader after segment indices are created
    material.opacityNode = float(0)

    return material
  }

  setMesh() {
    this.disposeMesh()

    // Generate 10 random paths
    this.paths = Array.from({ length: 45 }, () => this.generateRandomPath())

    const totalSegments = this.calculatePathIndices()
    const { spacing } = this.grid.settings
    const baseGeometry = this.createBaseGeometry(spacing, 0.005)
    const material = this.createMaterial()

    this.instancedMesh = new THREE.InstancedMesh(
      baseGeometry,
      material,
      totalSegments,
    )

    this.updatePathMatrices()
    this.updateSegmentIndices()
    this.updateOpacityShader(material)

    this.scene.add(this.instancedMesh)
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "🔲 Grid Lines",
      expanded: false,
    })

    folder.addBinding(this.settings, "delayMin", {
      label: "Delay Min",
      min: 0,
      max: 5,
      step: 0.1,
    })

    folder.addBinding(this.settings, "delayMax", {
      label: "Delay Max",
      min: 0,
      max: 5,
      step: 0.1,
    })

    folder.addBinding(this.settings, "durationMin", {
      label: "Duration Min",
      min: 0.1,
      max: 5,
      step: 0.1,
    })

    folder.addBinding(this.settings, "durationMax", {
      label: "Duration Max",
      min: 0.1,
      max: 5,
      step: 0.1,
    })

    folder
      .addButton({
        title: "Animate Paths",
      })
      .on("click", () => {
        this.animatePaths()
      })
  }
}
